import { z } from 'zod';
import { IsoDateInputSchema, IsoDateSchema, SlugInputSchema, SlugSchema } from './entities.js';

/**
 * Case Wiki — schéma `case_procedures` (M068).
 *
 * Une procédure = un volet contentieux sur le dossier (référé // fond //
 * appel // cassation…). Un dossier complexe a typiquement N procédures
 * parallèles, qui interagissent (le référé peut être suspendu en attente
 * du fond, l'appel intervient après le jugement…).
 *
 * Spec : docs/architecture/case-wiki/data-model.md §3.2.1.
 */

export const CaseProcedureTypeSchema = z.enum([
  'refere',
  'fond',
  'appel',
  'cassation',
  'instruction',
  'partie_civile',
  'mediation',
  'conciliation',
  'arbitrage',
  'jex',                  // juge de l'exécution
  'liquidation_judiciaire',
  'redressement_judiciaire',
  'sauvegarde',
  // Volets d'enquête (police + parquet, art. 53/75 CPP) — un cadre procédural
  // autonome, distinct de l'instruction. Ajout additif (M125). On NE modélise
  // QUE les régimes tranchés (arbitrage a, 2026-06-26) : une enquête de régime
  // indéterminé reste portée par `procedural_phase='enquete'` sur les faits,
  // SANS entité procédure — pas de `enquete` générique qui doublonnerait la
  // phase de fait.
  'enquete_flagrance',
  'enquete_preliminaire',
  'autre',
]);
export type CaseProcedureType = z.infer<typeof CaseProcedureTypeSchema>;

export const ProcedureStateSchema = z.enum([
  'en_cours', 'suspendue', 'radiee', 'cloturee',
]);
export type ProcedureState = z.infer<typeof ProcedureStateSchema>;

export const ProcedureOutcomeSchema = z.enum([
  'jugement', 'ordonnance', 'arret', 'non_lieu', 'classement',
  'desistement', 'transaction', 'radiation', 'en_cours', 'autre',
]);
export type ProcedureOutcome = z.infer<typeof ProcedureOutcomeSchema>;

/**
 * Lien juridique d'une procédure au dossier principal (décision ② — ancré CPP).
 * La saillance suit le lien, pas la co-mention dans une pièce :
 *  - `jointe`                     : jonction prononcée OU même n° RG → volet plein
 *  - `connexe_non_jointe`         : connexité art. 203 CPP, RG distinct → contexte rattaché non-actif
 *  - `mentionnee_sans_lien`       : co-mention sans connexité → contexte faible saillance
 *  - `autre_ordre_juridictionnel` : juridiction civile/admin → jamais de volet pénal
 */
export const ProcedureLienSchema = z.enum([
  'jointe',
  'connexe_non_jointe',
  'mentionnee_sans_lien',
  'autre_ordre_juridictionnel',
]);
export type ProcedureLien = z.infer<typeof ProcedureLienSchema>;

/**
 * Procédure complète comme retournée par `getProcedure` / chargée dans le
 * bundle. Toutes les colonnes de `case_procedures`.
 */
export const ProcedureSchema = z.object({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  slug: SlugSchema,
  label: z.string().min(3).max(200),
  procedure_type: CaseProcedureTypeSchema,
  juridiction: z.string().min(2).max(200).nullable(),
  rg_number: z.string().min(1).max(50).nullable(),
  parquet_number: z.string().nullable(),
  summary: z.string().max(4000).nullable(),
  introduction_date: IsoDateSchema.nullable(),
  closing_date: IsoDateSchema.nullable(),
  state: ProcedureStateSchema,
  outcome: ProcedureOutcomeSchema.nullable(),
  outcome_summary: z.string().max(500).nullable(),
  source_piece_slugs: z.array(SlugSchema).default([]),
  /**
   * Lien juridique au dossier (M126). `.nullable().default(null)` = read-relâché :
   * les rows antérieures à M126 n'ont pas la clé et doivent continuer de parser.
   */
  lien: ProcedureLienSchema.nullable().default(null),
  /**
   * Pattern transverse — `'agent'` quand l'agent a créé/maintenu la valeur,
   * `'user'` après édition manuelle (les ré-runs ne doivent pas l'écraser),
   * `'agent_validated'` = agent puis validé par avocat.
   */
  created_source: z.enum(['agent', 'user', 'agent_validated']).default('agent'),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  edited_by: z.string().uuid().nullable(),
  edited_at: z.string().datetime().nullable(),
});
export type Procedure = z.infer<typeof ProcedureSchema>;

/**
 * Payload du tool agent `resolve_or_create_procedure` (M068).
 * Toutes les colonnes optionnelles sauf `slug`, `label`, `procedure_type`.
 */
export const ResolveOrCreateProcedurePayloadSchema = z.object({
  slug: SlugInputSchema,
  label: z.string().min(3).max(200),
  procedure_type: CaseProcedureTypeSchema,
  juridiction: z.string().min(2).max(200).optional(),
  rg_number: z.string().min(1).max(50).optional(),
  parquet_number: z.string().optional(),
  summary: z.string().max(4000).optional(),
  introduction_date: IsoDateInputSchema.optional(),
  closing_date: IsoDateInputSchema.optional(),
  state: ProcedureStateSchema.optional(),
  outcome: ProcedureOutcomeSchema.optional(),
  outcome_summary: z.string().max(500).optional(),
  /**
   * Optional at the shared schema level for backwards-compatible update reads.
   * Create-time source enforcement belongs to the repository/tool path so an
   * existing procedure can still be updated without resending every source.
   */
  source_piece_slugs: z.array(SlugInputSchema).max(50).optional(),
  /** Lien juridique au dossier (décision ②) — qualifie la saillance du volet. */
  lien: ProcedureLienSchema.optional(),
});
export type ResolveOrCreateProcedurePayload = z.infer<typeof ResolveOrCreateProcedurePayloadSchema>;

/** Payload du tool agent `attach_fact_to_procedure`. */
export const AttachFactToProcedurePayloadSchema = z.object({
  fact_slug: SlugInputSchema,
  procedure_slug: SlugInputSchema,
});
export type AttachFactToProcedurePayload = z.infer<typeof AttachFactToProcedurePayloadSchema>;

/** Payload du tool agent `attach_facts_to_procedure`. */
export const AttachFactsToProcedurePayloadSchema = z.object({
  procedure_slug: SlugInputSchema,
  fact_slugs: z.array(SlugInputSchema).min(1).max(200),
});
export type AttachFactsToProcedurePayload = z.infer<typeof AttachFactsToProcedurePayloadSchema>;
